import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Typography, IconButton, Button } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles } from '@material-ui/core/styles';
import styles from './ContactsList.styles';

const useStyles = makeStyles(styles);

const promiseTimeout = (time, cb) => {
  return new Promise((res) => {
    setTimeout(() => {
      cb();
      res();
    }, time);
  });
};

function scrollDemo({ current, distance, totalTime, iterationsCount }) {
  const step = Math.floor(distance / iterationsCount);
  const timeStep = Math.floor(totalTime / iterationsCount);
  const callbacks = [];

  for (let i = 1; i <= iterationsCount; i++) {
    callbacks.push({
      time: timeStep,
      cb: () => {
        /* eslint-disable-next-line no-param-reassign */
        current.scrollTop = i * step;
      },
    });
  }

  for (let i = 0; i < iterationsCount; i++) {
    callbacks.push({
      time: timeStep,
      cb: () => {
        /* eslint-disable-next-line no-param-reassign */
        current.scrollTop = distance - i * step;
      },
    });
  }

  return callbacks.reduce((acc, { time, cb }) => acc.then(() => promiseTimeout(time, cb)), Promise.resolve());
}

const ContactsList = ({ items, fixedHeight, isArrowOnTop, onExpand, onItemClick }) => {
  const classes = useStyles({ isArrowOnTop });

  const [expanded, setExpanded] = React.useState(false);

  const onClickExpandHandler = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
    onExpand(!expanded);
  };

  const ref = React.createRef();

  const onContactClickHandler = (contact) => (e) => {
    e.stopPropagation();
    setExpanded(false);
    onExpand(false);
    onItemClick(contact);
  };

  React.useEffect(() => {
    if (expanded) {
      scrollDemo({ current: ref.current, distance: 20, totalTime: 200, iterationsCount: 20 });
    }
  }, [expanded]);

  const slicedItems = items.slice(1);

  return (
    <div className={fixedHeight ? classes.rootFixedHeight : classes.root} ref={ref}>
      <Typography variant="body2" classes={{ root: classes.summary }}>
        {items[0]}
      </Typography>
      {expanded && <div className={classes.highlighted} />}
      {expanded &&
        slicedItems.map((i) => (
          <div key={i}>
            <Button
              data-testid="contact-btn"
              classes={{
                root: classes.contactBtn,
                text: classes.contactBtnText,
              }}
              onClick={onContactClickHandler(i)}
              disableRipple
              disableFocusRipple
            >
              {i}
            </Button>
          </div>
        ))}
      {!!slicedItems.length && (
        <IconButton
          data-testid="expand-btn"
          onClick={onClickExpandHandler}
          aria-label="expand"
          className={cx(classes.btn, expanded && classes.expanded)}
        >
          <ExpandMoreIcon fontSize="inherit" />
        </IconButton>
      )}
    </div>
  );
};

ContactsList.propTypes = {
  /** List of contacts that will be rendered. */
  items: PropTypes.arrayOf(PropTypes.string),
  /** If true, maxHeight of 50px will be applied and scroll will appear. */
  fixedHeight: PropTypes.bool,
  /** Determines if arrow top position is zero */
  isArrowOnTop: PropTypes.bool,
  /** Callback for expanding list. */
  onExpand: PropTypes.func,
  /** Callback for clicking on contact. */
  onItemClick: PropTypes.func,
};

ContactsList.defaultProps = {
  items: [],
  fixedHeight: false,
  isArrowOnTop: false,
  onExpand: () => null,
  onItemClick: () => null,
};

export default ContactsList;
